/** Cash and commission offsets are separate ledger totals. Keep this module import-free. */
export interface PayoutSettlement {
  net: number;
  cash: number;
  recaptured: number;
  /** Signed: a negative remainder is a credit, not another payable commission. */
  remaining: number;
}

export const PAYOUT_SETTLEMENT_TOLERANCE_CENTS = 1;

export function moneyCents(value: number): number {
  if (!Number.isFinite(value)) {
    throw new Error('Invalid monetary amount');
  }
  const cents = Math.round((Math.abs(value) + Number.EPSILON) * 100);
  if (!Number.isSafeInteger(cents)) {
    throw new Error('Monetary amount exceeds supported precision');
  }
  return value < 0 ? -cents : cents;
}

export function payoutSettlement(net: number, cash: number, recaptured = 0): PayoutSettlement {
  const netCents = moneyCents(net);
  const cashCents = moneyCents(cash);
  const recapturedCents = moneyCents(recaptured);
  return {
    net: netCents / 100,
    cash: cashCents / 100,
    recaptured: recapturedCents / 100,
    remaining: (netCents - cashCents - recapturedCents) / 100
  };
}

export function settlementIsOutstanding(settlement: PayoutSettlement): boolean {
  return moneyCents(settlement.remaining) > PAYOUT_SETTLEMENT_TOLERANCE_CENTS;
}

/** Mirrors refresh_payout_payment_totals, including its no-payment/credit behavior. */
export function settlementPaymentStatus(settlement: PayoutSettlement): 'paid' | 'approved' {
  return moneyCents(settlement.cash) + moneyCents(settlement.recaptured) > 0
    && !settlementIsOutstanding(settlement) ? 'paid' : 'approved';
}

/** Strict currency input. Allocation equality is exact; payment settlement retains its cent tolerance. */
export function parseMoneyInput(value: string): number | null {
  const normalized = value.trim();
  if (!/^(?:\d+(?:\.\d{1,2})?|\.\d{1,2})$/.test(normalized)) {
    return null;
  }
  const amount = Number(normalized);
  return Number.isFinite(amount) && Number.isSafeInteger(Math.round(amount * 100)) ? amount : null;
}
